
import { Message } from '@/types/chat';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { differenceInDays } from 'date-fns';

/**
 * Hook for handling report processing operations
 */
export const useReportProcessing = () => {
  const { toast } = useToast();

  /**
   * Process admin user messages
   */
  const handleAdminMessage = async (
    message: string, 
    messages: Message[], 
    user: any
  ): Promise<{ response: string, success: boolean }> => {
    try {
      console.log('Processing admin message, bypassing all limits');
      
      // Use generate-report function with admin flag set to true
      const { data, error } = await supabase.functions.invoke('generate-report', {
        body: { 
          topic: message, 
          previousMessages: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          isAdmin: true,
          userId: user?.id
        }
      });

      if (error) {
        console.error('Error processing admin message:', error);
        return { 
          response: "I encountered an error processing your request. Please try again.", 
          success: false 
        };
      }

      return { 
        response: data, 
        success: true 
      };
    } catch (err) {
      console.error('Error in admin message processing:', err);
      // Improved error message with network troubleshooting suggestion
      return { 
        response: "There was a connection error reaching our AI services. This might be due to temporary network issues or service maintenance. Please try again in a few moments.", 
        success: false 
      };
    }
  };

  /**
   * Process regular user messages and handle report generation
   */
  const processUserMessage = async (
    message: string, 
    messages: Message[], 
    isFirstReport: boolean
  ): Promise<{ response: string, success: boolean }> => {
    try {
      // Make the API call to process the message
      const { data, error } = await supabase.functions.invoke('generate-report', {
        body: { 
          topic: message, 
          previousMessages: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          isFreeReport: isFirstReport
        }
      });

      if (error) {
        console.error('Error generating report:', error);
        // Check for specific error types
        if (error.message?.includes('timeout') || error.message?.includes('Failed to fetch')) {
          return { 
            response: "The report generation service is currently experiencing connectivity issues. This could be due to high demand or network problems. Please try again in a few moments.", 
            success: false 
          };
        }
        return { 
          response: "I'm sorry, but I encountered an error generating your report. Please try again.", 
          success: false 
        };
      }

      return { 
        response: data, 
        success: true 
      };
    } catch (err) {
      console.error('Error processing user message:', err);
      // Check if this is a network error
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('network') || 
          errorMessage.includes('timeout') || errorMessage.includes('abort')) {
        // Network-related error
        return { 
          response: "I couldn't connect to our report generation service. Please check your internet connection and try again. If the problem persists, our service might be experiencing temporary issues.", 
          success: false 
        };
      }
      
      return { 
        response: "There was an unexpected error. Please try again later.", 
        success: false 
      };
    }
  };

  /**
   * Check if user's reports should be reset based on last_reset_at timestamp
   * If it's been 30+ days since the last reset, reports are reset to 0
   */
  const checkAndResetReports = async (userId: string): Promise<boolean> => {
    try {
      // Get the user's report usage data
      const { data, error } = await supabase
        .from('report_usage')
        .select('reports_generated, last_reset_at')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No record found, create one with default values
          const { error: insertError } = await supabase
            .from('report_usage')
            .insert({ 
              user_id: userId, 
              reports_generated: 0,
              last_reset_at: new Date().toISOString() 
            });
            
          if (insertError) {
            console.error('Error creating report usage record:', insertError);
          }
          return false; // Nothing to reset for new users
        } else {
          console.error('Error checking report usage for reset:', error);
          return false;
        }
      }

      if (!data || !data.last_reset_at) return false;
      
      // Check if it's been 30+ days since the last reset
      const lastResetDate = new Date(data.last_reset_at);
      const today = new Date();
      const daysSinceReset = differenceInDays(today, lastResetDate);
      
      if (daysSinceReset >= 30) {
        console.log(`It's been ${daysSinceReset} days since the last reset. Resetting report count.`);
        
        // Reset the user's report count and update last_reset_at
        const { error: resetError } = await supabase
          .from('report_usage')
          .update({ 
            reports_generated: 0, 
            last_reset_at: today.toISOString() 
          })
          .eq('user_id', userId);
          
        if (resetError) {
          console.error('Error resetting report count:', resetError);
          return false;
        }
        
        console.log('Successfully reset report count for user:', userId);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error in checkAndResetReports:', err);
      return false;
    }
  };

  /**
   * Check if user has hit rate limit for requests
   */
  const checkRateLimit = async (isAdmin: boolean = false): Promise<{ limited: boolean, message: string }> => {
    try {
      // Admin users bypass rate limits entirely
      if (isAdmin) {
        console.log('Admin user detected, bypassing rate limits');
        return {
          limited: false,
          message: ''
        };
      }
      
      const { data, error } = await supabase.functions.invoke('check-rate-limit', {
        body: { action: 'chat' }
      });

      if (error) {
        console.error('Error checking rate limit:', error);
        // Don't block the user if rate limit check fails
        return {
          limited: false,
          message: ''
        };
      }

      return {
        limited: data.limited || false,
        message: data.message || "You've reached the rate limit. Please try again later."
      };
    } catch (err) {
      console.error('Error in rate limit check:', err);
      // If rate limit check fails, allow the user to continue
      return {
        limited: false,
        message: ''
      };
    }
  };

  return {
    handleAdminMessage,
    processUserMessage,
    checkRateLimit,
    checkAndResetReports
  };
};
